"use client";

import { useState, useEffect } from "react";
import Link from "next/link";

export const COOKIE_CONSENT_KEY = "mc_cookie_consent";
export type ConsentValue = "all" | "essential";

export function getCookieConsent(): ConsentValue | null {
  if (typeof window === "undefined") return null;
  const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (stored === "all" || stored === "essential") return stored;
  return null;
}

/**
 * Dispatch after the user saves a choice so analytics can start when consent is "all".
 */
export function dispatchCookieConsent(value: ConsentValue) {
  window.dispatchEvent(new CustomEvent("mc:cookie-consent", { detail: { value } }));
}

/** Fixed dark panel colors — do not use theme CSS vars (home/marketing default to light text tokens). */
const panel = {
  bg: "rgba(10, 18, 36, 0.97)",
  border: "rgba(163, 202, 211, 0.16)",
  title: "#F1F5F9",
  body: "#94A3B8",
  strong: "#E2E8F0",
  muted: "#64748B",
  accent: "#A3CAD3",
  btnSecondaryBorder: "rgba(163, 202, 211, 0.22)",
};

/**
 * Auto-consent cookie notice bar.
 *
 * Cookies (analytics + advertising) are granted automatically on page load.
 * A slim notice bar is shown at the bottom so users are informed and can
 * open Cookie Settings to opt-out if they wish.
 *
 * The bar auto-dismisses after 8 seconds or when the user closes it.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [adCookies, setAdCookies] = useState(true);

  useEffect(() => {
    const stored = getCookieConsent();

    // Auto-consent: grant "all" immediately if not previously stored
    if (!stored) {
      localStorage.setItem(COOKIE_CONSENT_KEY, "all");
      dispatchCookieConsent("all");
      // Show the notice bar briefly
      const showTimer = setTimeout(() => setVisible(true), 400);
      // Auto-dismiss after 8 seconds
      const hideTimer = setTimeout(() => setVisible(false), 8400);
      return () => {
        clearTimeout(showTimer);
        clearTimeout(hideTimer);
      };
    }
  }, []);

  function dismiss() {
    setVisible(false);
  }

  function saveSettings() {
    const value: ConsentValue = adCookies ? "all" : "essential";
    localStorage.setItem(COOKIE_CONSENT_KEY, value);
    dispatchCookieConsent(value);
    setShowSettings(false);
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <>
      {/* ─── Notice Bar ─── */}
      <div className="fixed bottom-0 inset-x-0 z-50 pointer-events-none">
        <div
          className="pointer-events-auto w-full px-4 sm:px-6 py-3"
          style={{
            background: panel.bg,
            borderTop: `1px solid ${panel.border}`,
            backdropFilter: "blur(16px)",
            animation: "fadeSlideUp 0.3s ease-out",
          }}
        >
          <div className="mx-auto max-w-7xl flex items-center gap-3 sm:gap-4">
            <p
              className="flex-1 text-[11px] sm:text-[12px] leading-relaxed"
              style={{ color: panel.body }}
            >
              This site uses cookies and similar technologies (&quot;cookies&quot;) as strictly
              necessary for site operation. We and our partners also would like to set additional
              cookies to ensure site performance analytics, functionality, advertising and social
              media features. See our{" "}
              <Link
                href="/privacy"
                className="font-medium underline underline-offset-2"
                style={{ color: panel.accent }}
              >
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link
                href="/privacy#cookies"
                className="font-medium underline underline-offset-2"
                style={{ color: panel.accent }}
              >
                Cookie Policy
              </Link>{" "}
              for details. You can change your preferences in our Cookie Settings.
            </p>

            <button
              type="button"
              onClick={() => setShowSettings(true)}
              className="shrink-0 px-4 py-2 text-[12px] font-semibold rounded-lg transition-all hover:brightness-110"
              style={{
                border: `1px solid ${panel.btnSecondaryBorder}`,
                color: panel.title,
                background: "transparent",
              }}
            >
              Cookie Settings
            </button>

            <button
              type="button"
              onClick={dismiss}
              className="shrink-0 px-5 py-2 text-[12px] font-semibold rounded-lg transition-all hover:brightness-110"
              style={{
                background: panel.accent,
                color: "#0A0F1E",
              }}
            >
              Got it
            </button>
          </div>
        </div>
      </div>

      {/* ─── Cookie Settings Modal ─── */}
      {showSettings && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
        >
          <div
            className="relative w-full max-w-lg rounded-2xl p-6 sm:p-8"
            style={{
              background: panel.bg,
              border: `1px solid ${panel.border}`,
              boxShadow: "0 24px 48px rgba(0,0,0,0.55)",
              animation: "fadeSlideUp 0.25s ease-out",
              color: panel.body,
            }}
          >
            {/* Close */}
            <button
              type="button"
              onClick={() => setShowSettings(false)}
              className="absolute top-4 right-4 p-1.5 rounded-md transition-colors"
              style={{ color: panel.muted }}
              aria-label="Close cookie settings"
            >
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>

            <h3 className="text-[16px] font-bold" style={{ color: panel.title }}>
              Cookie Notice
            </h3>

            <p className="mt-3 text-[12px] sm:text-[13px] leading-relaxed" style={{ color: panel.body }}>
              We and our advertising and social media partners use cookies to collect information
              about your device and activity on our sites and other online services. You can
              opt-out of advertising cookies by turning off the toggle below. See our{" "}
              <Link
                href="/privacy"
                className="font-medium underline underline-offset-2"
                style={{ color: panel.accent }}
              >
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link
                href="/privacy#cookies"
                className="font-medium underline underline-offset-2"
                style={{ color: panel.accent }}
              >
                Cookie Policy
              </Link>.
            </p>

            {/* Cookie toggles */}
            <div className="mt-5 space-y-0 rounded-xl overflow-hidden" style={{ border: `1px solid ${panel.border}` }}>
              {/* Strictly Necessary */}
              <div
                className="flex items-center justify-between px-4 py-3.5"
                style={{ borderBottom: `1px solid ${panel.border}` }}
              >
                <span className="text-[13px] font-medium" style={{ color: panel.title }}>
                  Strictly Necessary Cookies
                </span>
                <span className="text-[11px] font-semibold" style={{ color: panel.accent }}>
                  Always Active
                </span>
              </div>

              {/* Advertising & Social Media */}
              <div className="flex items-center justify-between px-4 py-3.5">
                <span className="text-[13px] font-medium" style={{ color: panel.title }}>
                  Advertising and Social Media Cookies
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={adCookies}
                  onClick={() => setAdCookies(!adCookies)}
                  className="relative shrink-0 w-[44px] h-[24px] rounded-full transition-colors duration-200"
                  style={{
                    background: adCookies ? panel.accent : "rgba(100,116,139,0.35)",
                  }}
                >
                  <span
                    className="absolute top-[2px] left-[2px] w-[20px] h-[20px] rounded-full bg-white transition-transform duration-200"
                    style={{
                      transform: adCookies ? "translateX(20px)" : "translateX(0)",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    }}
                  />
                </button>
              </div>
            </div>

            {/* Save */}
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={saveSettings}
                className="px-6 py-2.5 text-[13px] font-semibold rounded-lg transition-all hover:brightness-110"
                style={{
                  background: panel.accent,
                  color: "#0A0F1E",
                }}
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
